import React from "react";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { wire } from "react-hot-wire";
import { isUUID } from "validator";

import isApplicationName from "utils/validation/isApplicationName";
import isDomainName from "utils/validation/isDomainName";

import { SiteService } from "services/site/Site.service";

import Drawer from "components/common/drawer/Drawer";

import CreateApplication from "./Create.template";
import { ApplicationType } from "../../../models/application/Application";

export type CreateApplicationFormValues = {
  name: string;
  environment: string;
  mainDomainName: string | null;
  note: string | null;
  accountManagerNote: string | null;
  redirectDomainNames: string[];
  packages: string[];
  rewardCafeteriaEnabled: boolean;
  workflowsEnabled: boolean;
  oldAdminEnabled: boolean;
  isEnterprise: boolean;
  billingEmail: string;
  billingForename: string;
  billingSurname: string;
  currency: string;
  hubSpotId: string;
  featureFlags: {
    battlesEnabled: boolean;
  };
};

const CreateApplicationSchema = Yup.object().shape({
  name: Yup.string()
    .test(
      "application-name-validation",
      "invalid-application-name",
      isApplicationName()
    )
    .required(),
  environment: Yup.string().required(),
  mainDomainName: Yup.string().test(
    "domain-validation",
    "invalid-domain",
    isDomainName(true)
  ),
  note: Yup.string().required(),
  accountManagerNote: Yup.string().required(),
  redirectDomainNames: Yup.array().of(
    Yup.string().test("domain-validation", "invalid-domain", isDomainName(true))
  ),
  packages: Yup.array().of(
    Yup.string().test("uuid-validation", "invalid-uuid", isUUID)
  ),
  rewardCafeteriaEnabled: Yup.boolean().required(),
  workflowsEnabled: Yup.boolean().required(),
  oldAdminEnabled: Yup.boolean().required(),
  isEnterprise: Yup.boolean().required(),
  billingEmail: Yup.string()
    .email()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string()
        .email()
        .required()
    }),
  billingForename: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  billingSurname: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  currency: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  hubSpotId: Yup.string().nullable(),
  featureFlags: Yup.object().shape({
    battlesEnabled: Yup.boolean().required()
  })
});

function CreateApplicationComponent({
  siteService,
  drawerProps,
  action
}: {
  siteService: SiteService;
  drawerProps?: any;
  action?: () => void;
}) {
  const site = siteService.defaultSite();

  return (
    <Drawer
      {...drawerProps}
      render={({ close }: { close: () => void }) => (
        <Formik
          initialValues={{
            redirectDomainNames: [],
            packages: [],
            mainDomainName: "",
            name: "",
            note: "",
            accountManagerNote: "",
            environment: "live",
            rewardCafeteriaEnabled: false,
            workflowsEnabled: false,
            oldAdminEnabled: true,
            isEnterprise: false,
            billingEmail: "",
            billingForename: "",
            billingSurname: "",
            currency: "usd",
            hubSpotId: "",
            featureFlags: {
              battlesEnabled: false
            }
          }}
          validationSchema={CreateApplicationSchema}
          onSubmit={({
            isEnterprise,
            ...values
          }: CreateApplicationFormValues) =>
            siteService
              .createApplication({
                values: {
                  ...values,
                  mainDomainName: values.mainDomainName
                    ? values.mainDomainName
                    : null,
                  applicationType: isEnterprise
                    ? ApplicationType.ENTERPRISE
                    : ApplicationType.NOTOUCH
                }
              })
              .then(close)
              .then(action)
          }
          render={(props: FormikProps<CreateApplicationFormValues>) => (
            <CreateApplication
              formikProps={props}
              site={site}
              close={close}
              translationPath="application.create"
            />
          )}
        />
      )}
    />
  );
}

export default wire(["siteService"], CreateApplicationComponent);
